import React from 'react';
import {
  Bot,
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  MapPin,
  MessageSquare,
  Mic,
  MousePointerClick,
  Navigation,
  Phone,
  Smartphone,
  UserRound,
  Video,
} from 'lucide-react';

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('es-PE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function senderLabel(message) {
  const labels = {
    admin: 'Admin',
    store: 'Tienda',
    provider: 'Proveedor',
    driver: 'Motorizado',
    system: 'Sistema',
    final_customer: 'Cliente final',
    external_whatsapp: 'WhatsApp externo',
  };
  return labels[message.sender_type] || message.sender_type || 'Mensaje';
}

function fileExt(url = '') {
  const clean = String(url || '').split('?')[0].toLowerCase();
  const index = clean.lastIndexOf('.');
  return index >= 0 ? clean.slice(index) : '';
}

function inferType(message) {
  const explicit = String(message.message_type || message.ai_metadata?.whatsapp_message_type || '').toLowerCase();
  if (explicit) return explicit;
  if (message.latitude != null && message.longitude != null) return 'location';
  if (!message.media_url) return 'text';
  const ext = fileExt(message.media_url);
  if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) return 'image';
  if (['.ogg', '.opus', '.mp3', '.m4a', '.aac', '.wav', '.amr'].includes(ext)) return 'audio';
  if (['.mp4', '.mov', '.webm'].includes(ext)) return 'video';
  return 'document';
}

function channelIcon(message, type) {
  if (message.channel === 'system') return <Bot className="h-3.5 w-3.5" />;
  if (type === 'location' || type === 'live_location') return <MapPin className="h-3.5 w-3.5" />;
  if (type === 'image' || type === 'sticker') return <ImageIcon className="h-3.5 w-3.5" />;
  if (type === 'audio') return <Mic className="h-3.5 w-3.5" />;
  if (type === 'video') return <Video className="h-3.5 w-3.5" />;
  if (type === 'document') return <FileText className="h-3.5 w-3.5" />;
  if (type === 'contact') return <UserRound className="h-3.5 w-3.5" />;
  if (type === 'interactive' || type === 'button') return <MousePointerClick className="h-3.5 w-3.5" />;
  if (message.channel === 'whatsapp') return <Smartphone className="h-3.5 w-3.5" />;
  return <MessageSquare className="h-3.5 w-3.5" />;
}

function getPayload(message) {
  return message.ai_metadata?.whatsapp_payload || message.ai_metadata?.whatsapp_contact_payload || null;
}

function contactList(message) {
  const payload = getPayload(message);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(message.contact_payload)) return message.contact_payload;
  if (Array.isArray(payload?.contacts)) return payload.contacts;
  return [];
}

function ContactCard({ message, isMine }) {
  const contacts = contactList(message);
  if (!contacts.length) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-extrabold">
        <UserRound className="h-4 w-4" />
        Contacto compartido
      </div>
      {contacts.map((contact, index) => {
        const name = contact?.name?.formatted_name || contact?.name?.first_name || `Contacto ${index + 1}`;
        const phones = (contact?.phones || []).map(phone => phone.phone || phone.wa_id).filter(Boolean);
        return (
          <div key={`${name}-${index}`} className={`rounded-xl p-3 ${isMine ? 'bg-white/10' : 'border border-slate-200 bg-white'}`}>
            <div className="font-bold">{name}</div>
            {phones.length ? phones.map(phone => {
              const digits = String(phone).replace(/\D/g, '');
              const waNumber = digits.startsWith('51') ? digits : `51${digits.slice(-9)}`;
              return (
                <a
                  key={phone}
                  href={`https://wa.me/${waNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className={`mt-2 inline-flex items-center gap-2 text-xs font-semibold underline ${isMine ? 'text-white' : 'text-[#B1336E]'}`}
                >
                  <Phone className="h-3.5 w-3.5" />
                  {phone}
                </a>
              );
            }) : <div className="mt-1 text-xs opacity-70">Sin telefono</div>}
          </div>
        );
      })}
    </div>
  );
}

function LocationCard({ message, type, isMine }) {
  if (message.latitude == null || message.longitude == null) return null;
  const link = `https://www.google.com/maps?q=${message.latitude},${message.longitude}`;
  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      className={`block rounded-xl border p-3 ${isMine ? 'border-white/20 bg-white/10 text-white' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}
    >
      <div className="flex items-center gap-2 font-extrabold">
        {type === 'live_location' ? <Navigation className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
        {type === 'live_location' ? 'Ubicacion en vivo' : 'Ubicacion compartida'}
      </div>
      <div className="mt-1 text-xs opacity-80">{message.content || `${message.latitude}, ${message.longitude}`}</div>
      <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold underline">
        Abrir mapa <ExternalLink className="h-3 w-3" />
      </div>
    </a>
  );
}

function AttachmentCard({ message, type, isMine }) {
  if (!message.media_url) return null;
  if (type === 'image' || type === 'sticker') {
    return (
      <a href={message.media_url} target="_blank" rel="noreferrer" className="block">
        <div className={`overflow-hidden rounded-xl ${isMine ? 'bg-white/10' : 'bg-slate-100'}`}>
          <img src={message.media_url} alt={type === 'sticker' ? 'Sticker WhatsApp' : 'Adjunto'} className="max-h-72 w-full object-contain" />
        </div>
        {type === 'sticker' && <div className="mt-1 text-xs font-semibold opacity-75">Sticker WhatsApp</div>}
      </a>
    );
  }
  if (type === 'audio') {
    return (
      <div className="min-w-[240px]">
        <div className="mb-2 flex items-center gap-2 text-sm font-extrabold">
          <Mic className="h-4 w-4" />
          Audio
        </div>
        <audio controls preload="metadata" src={message.media_url} className="w-full" />
      </div>
    );
  }
  if (type === 'video') {
    return (
      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-extrabold">
          <Video className="h-4 w-4" />
          Video
        </div>
        <video controls preload="metadata" src={message.media_url} className="max-h-80 w-full rounded-xl bg-black" />
      </div>
    );
  }
  return (
    <a
      href={message.media_url}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-3 rounded-xl border p-3 ${isMine ? 'border-white/20 bg-white/10 text-white' : 'border-slate-200 bg-white text-slate-900'}`}
    >
      <FileText className="h-5 w-5" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-extrabold">{message.content || 'Documento adjunto'}</div>
        <div className="text-xs opacity-70">Abrir archivo</div>
      </div>
      <Download className="h-4 w-4" />
    </a>
  );
}

function WhatsAppStructuredCard({ message, type, isMine }) {
  const payload = getPayload(message) || {};
  if (!['interactive', 'button', 'reaction', 'order', 'referral', 'system', 'unsupported'].includes(type)) return null;
  const titles = {
    interactive: 'Respuesta interactiva',
    button: 'Boton WhatsApp',
    reaction: 'Reaccion',
    order: 'Orden WhatsApp',
    referral: 'Referencia WhatsApp',
    system: 'Sistema WhatsApp',
    unsupported: 'Tipo WhatsApp no soportado',
  };
  const detail =
    payload?.interactive?.button_reply?.title ||
    payload?.interactive?.list_reply?.title ||
    payload?.button?.text ||
    payload?.button?.payload ||
    payload?.reaction?.emoji ||
    payload?.referral?.headline ||
    payload?.referral?.source_url ||
    message.content ||
    type;
  return (
    <div className={`rounded-xl border p-3 ${isMine ? 'border-white/20 bg-white/10' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-2 text-sm font-extrabold">
        <MousePointerClick className="h-4 w-4" />
        {titles[type] || 'Mensaje WhatsApp'}
      </div>
      {detail && <div className="mt-1 whitespace-pre-wrap text-xs opacity-80">{detail}</div>}
    </div>
  );
}

export default function MessageBubble({ message, currentUserType = 'admin' }) {
  const type = inferType(message);
  const isMine = message.sender_type === currentUserType && message.channel !== 'system';
  const isSystem = message.channel === 'system' || message.sender_type === 'system';
  const isWhatsApp = message.channel === 'whatsapp';
  const isFastMaxApp = message.channel === 'fastmax_app';

  const bubbleClass = isSystem
    ? 'bg-amber-50 border-amber-200 text-amber-900 mx-auto'
    : isMine
      ? 'bg-[#B1336E] text-white border-[#B1336E] ml-auto'
      : isWhatsApp
        ? 'bg-emerald-50 border-emerald-200 text-emerald-950 mr-auto'
        : 'bg-white border-slate-200 text-slate-900 mr-auto';

  const hasSpecialContent =
    (message.media_url && ['image', 'sticker', 'audio', 'video', 'document'].includes(type)) ||
    (['location', 'live_location'].includes(type) && message.latitude != null && message.longitude != null) ||
    (type === 'contact' && contactList(message).length > 0) ||
    ['interactive', 'button', 'reaction', 'order', 'referral', 'system', 'unsupported'].includes(type);
  const contentIsRenderedInsideCard =
    type === 'document' ||
    (type === 'contact' && contactList(message).length > 0) ||
    ['interactive', 'button', 'reaction', 'order', 'referral', 'system', 'unsupported'].includes(type);

  return (
    <div className={`max-w-[92%] rounded-2xl border px-3 py-2 shadow-sm sm:max-w-[82%] ${bubbleClass}`}>
      <div className={`mb-1 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold ${isMine ? 'text-white/80' : 'text-slate-500'}`}>
        {channelIcon(message, type)}
        <span>{senderLabel(message)}</span>
        {isWhatsApp && <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-emerald-700">WhatsApp</span>}
        {isFastMaxApp && <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-sky-700">Fast Max App</span>}
        {message.channel === 'internal' && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600">Interno</span>}
        {message.message_type === 'note' && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600">Nota</span>}
        {message.visibility === 'admin_only' && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-amber-700">Admin</span>}
        {message.ai_metadata?.match_confidence === 'ambiguous' && <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-red-700">Match ambiguo</span>}
      </div>

      <div className="space-y-2">
        <AttachmentCard message={message} type={type} isMine={isMine} />
        <LocationCard message={message} type={type} isMine={isMine} />
        {type === 'contact' && <ContactCard message={message} isMine={isMine} />}
        <WhatsAppStructuredCard message={message} type={type} isMine={isMine} />

        {message.content && !contentIsRenderedInsideCard && (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed font-montserrat">{message.content}</p>
        )}

        {!message.content && !hasSpecialContent && (
          <div className="flex items-center gap-2 text-sm">
            <UserRound className="h-4 w-4" />
            {message.message_type || 'Mensaje'}
          </div>
        )}
      </div>

      <div className={`mt-1 text-[10px] ${isMine ? 'text-white/70' : 'text-slate-400'}`}>
        {formatTime(message.created_at || message.timestamp)}
      </div>
    </div>
  );
}
